// ABOUTME: Modal component for selecting a timeslot for BOL replenishment
// ABOUTME: Fetches available timeslots and allows user to select and save one

import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { useGetAvailableTimeslotsQuery, useSetBolreplenishmentTimeslotMutation } from "../../../newapi/webshop/bolreplenishmentSlice";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import ShValidatedInputDropdown from "../../theme/dropdowns/ShValidatedInputDropdown";
import * as yup from "yup";
import { yupRequired } from "../../../utils/validation";

const MSelectTimeslot = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [formattedTimeslots, setFormattedTimeslots] = useState([]);

    const [setBolreplenishmentTimeslot, { isLoading }] = useSetBolreplenishmentTimeslotMutation();
    const { data: timeslots, isLoading: timeslotsLoading } = useGetAvailableTimeslotsQuery(props.bolReplenishmentId, {
        skip: !props.bolReplenishmentId
    });

    const [selectedTimeslot, setSelectedTimeslot] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});

    const handleClose = () => {
        setSelectedTimeslot("");
        setFieldErrors({});
        props.handleClose();
    };

    const rules = {
        dateSlot: yupRequired('timeslot', t)
    };

    const schema = yup.object().shape(rules);

    const saveTimeslot = async () => {
        try {
            if (isLoading) return;

            setFieldErrors({});
            await schema.validate({ dateSlot: selectedTimeslot }, { abortEarly: false });

            const response = await setBolreplenishmentTimeslot({
                id: props.bolReplenishmentId,
                dateSlot: selectedTimeslot
            }).unwrap();

            showToastMessagePostRequest(t, response);
            handleClose();
        } catch (err) {
            if (err.name === 'ValidationError') {
                const errors = err.inner.reduce((acc, currentError) => ({
                    ...acc,
                    [currentError.path]: currentError.message
                }), {});
                setFieldErrors(errors);
            } else {
                showToastMessageRequestError(t("save_failed"), t, err);
            }
        }
    };

    const handleChange = (key, value) => {
        setSelectedTimeslot(value);
        setFieldErrors({
            ...fieldErrors,
            [key]: ''
        });
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    useEffect(() => {
        if(timeslots?.data && timeslots?.data?.length > 0){
            const formatted = timeslots?.data?.map(slot => ({
                id: slot,
                name: slot
            })) || [];
            setFormattedTimeslots(formatted);
        }
    },[timeslots, timeslotsLoading])

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1
                }}
            >
                <Subheading>{t("select_timeslot")}</Subheading>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2
                }}
            >
                <Box
                    sx={{
                        width: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 7
                    }}
                >
                    <Box
                        sx={{
                            width: 120,
                        }}
                    >
                        <Text>{t("timeslot")}</Text>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                            borderRadius: 4,
                            display: "flex",
                            height: 32
                        }}
                    >
                        <ShValidatedInputDropdown
                            name="dateSlot"
                            displayName="name"
                            changeField="id"
                            options={formattedTimeslots || []}
                            noSelection={t("choose_timeslot")}
                            onChange={handleChange}
                            selected={formattedTimeslots?.findIndex(slot => slot.id === selectedTimeslot) ?? -1}
                            error={fieldErrors.dateSlot}
                            loading={timeslotsLoading}
                        />
                    </Box>
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display: "flex",
                    alignItems: "center",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    justifyContent: "space-between",
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    }
                }}
            >
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={handleClose}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon/>}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        isLoading={isLoading}
                        className={"group-btn"}
                        color={"green"}
                        variant="contained"
                        onClick={saveTimeslot}
                        disabled={!selectedTimeslot}
                    >
                        {t("save")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MSelectTimeslot;